import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { ModalForm } from 'components/ModalForm';
import { useModal } from 'context/ModalProvider/useModal';
import { ValidationGroup } from 'devextreme-react';
import DataSource from 'devextreme/data/data_source';
import $createDataSource from 'utils/create-data-souce-instance';
import { isSuccess } from 'utils/responseStatus';

import {
  useChangeContractorPumpPriceMutation,
  useLazyGetContractorPumpPriceDetailQuery,
} from '../../api/ContractorPumpPrice';
import { ContractorPumpPriceForm } from '../../components';
import { CONTRACTOR_PUMP_PRICE_CRATE } from '../../ContractorDeliveryPrice.constants';
import { ContractorPumpPriceCreate, ContractorPumpPriceUpdate } from '../../ContractorPumpPrice.entity';

type Props = {
  onRefresh: () => void;
  contractorId: number | string;
  id: number;
};

export const EditContractorPumpPriceModal: FC<Props> = ({ onRefresh, contractorId, id }) => {
  const { hideModal } = useModal();
  const modalValidateRef = useRef<ValidationGroup>(null);

  const [form, setForm] = useState<ContractorPumpPriceCreate>(CONTRACTOR_PUMP_PRICE_CRATE);

  const [detContractorPumpPriceDetail] = useLazyGetContractorPumpPriceDetailQuery();
  const [changeContractorPumpPrice] = useChangeContractorPumpPriceMutation();

  const factoryDataStore: DataSource = useMemo(() => {
    const defaultFilter = ['parentId', '=', null];
    return $createDataSource('/v1/factory', 'id', { pageSize: 20, paginate: true, defaultFilter });
  }, []);

  const getContractorDetail = useCallback(async () => {
    detContractorPumpPriceDetail({ id })
      .unwrap()
      .then((data) => {
        if (isSuccess(data)) {
          setForm(data);
        }
      });
  }, [detContractorPumpPriceDetail, id]);

  useEffect(() => {
    getContractorDetail();
  }, [getContractorDetail]);

  const handleChange = (
    name: keyof ContractorPumpPriceCreate,
    value: ContractorPumpPriceCreate[keyof ContractorPumpPriceCreate],
  ) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const objectDataStore: DataSource = useMemo(() => {
    const defaultFilter = [['parentId', '=', null], 'and', ['type', '=', 1]];
    return $createDataSource(`/v1/ContractorObject/${contractorId}`, 'id', { defaultFilter });
  }, [contractorId]);

  const handleSubmit = () => {
    const isValid = modalValidateRef.current?.instance.validate().isValid;

    if (!isValid || !form) return;

    const updateData: ContractorPumpPriceUpdate = {
      ...form,
      id,
      contractorId: Number(contractorId),
    };

    changeContractorPumpPrice(updateData).then((data) => {
      if (isSuccess(data)) {
        onRefresh();
        hideModal();
        toast.success('Цена за насос успешно изменена', {
          position: 'bottom-right',
        });
      }
    });
  };

  return (
    <ModalForm
      onHidden={hideModal}
      title="Редактирование цены за насос"
      visible
      isLoadingSubmit={false}
      width={800}
      height="auto"
      textConfirm="Сохранить"
      hideOnOutsideClick={false}
      onSubmit={handleSubmit}
      ref={modalValidateRef}
    >
      <ContractorPumpPriceForm
        form={form}
        onChange={handleChange}
        factories={factoryDataStore}
        storeObject={objectDataStore}
        type="EDIT"
      />
    </ModalForm>
  );
};
